import React from 'react';
import CourseBlock from './CourseBlock';
import { dayEntryKey, layoutOverlappingEvents, overlapLayoutToColumnStyle,
  type OverlapLayout } from '../../lib/scheduleLayout';

export interface TimeSlot {
  hour: number;
  minute: number;
}

interface DayColumnProps {
  day: Date;
  dayIndex: number; // 0..6
  timeSlots: TimeSlot[];
  courses: any[]; // EnhancedCourse[]
  calcTop: (time: string) => number;
  calcHeight: (start: string, end: string) => number;
  onContextMenuSlot: (e: React.MouseEvent, day: Date, hour: number, minute: number) => void;
  onSlotClick?: (day: Date, hour: number, minute: number) => void;
  onContextMenuCourse: (e: React.MouseEvent, course: any) => void;
  onClickCourse?: (course: any) => void;
  onDragOverSlot?: (e: React.DragEvent<HTMLDivElement>, day: Date, hour: number, minute: number) => void;
  onDropSlot?: (e: React.DragEvent<HTMLDivElement>, day: Date, hour: number, minute: number) => void;
  isDraggingCourse?: boolean;
  activeDropTarget?: { dayKey: string; hour: number; minute: number } | null;
  activeDropConflictStatus?: 'checking' | 'ok' | 'conflict' | 'availability_override' | 'error' | null;
  dayKey?: string;
  draggable?: boolean;
  onCourseDragStart?: (e: React.DragEvent<HTMLDivElement>, course: any) => void;
  onCourseDragEnd?: () => void;
  onTrialDragStart?: (e: React.DragEvent<HTMLButtonElement>, trial: any) => void;
  onTrialDragEnd?: () => void;
  getContingentInfo?: (studentName: string) => { remainingUnits: number; totalUnits: number } | null;
  getContingentColor?: (remainingUnits: number, totalUnits: number) => string;
  children?: React.ReactNode;
  trials?: any[]; // TrialLessonDTO[] - Probestunden für diesen Tag
  onContextMenuTrial?: (e: React.MouseEvent, trial: any) => void;
  rowHeight?: number;
  availabilitySlots?: Array<{ dayOfWeek: number; time_start: string; time_end: string }>;
  /** Gemeinsame Anordnung von Kursen und Probestunden dieses Tages. */
  dayLayout?: Map<string, OverlapLayout>;
}

function normalizeToHHMM(t: string): string {
  if (!t) return '';
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = normalizeToHHMM(hhmm).split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

function isWithinAvailability(dayOfWeek: number, slotHHMM: string, availabilitySlots: Array<{ dayOfWeek: number; time_start: string; time_end: string }> | undefined): boolean {
  if (!availabilitySlots || availabilitySlots.length === 0) return true;
  const daySlots = availabilitySlots.filter((s) => Number(s.dayOfWeek) === Number(dayOfWeek));
  if (daySlots.length === 0) return false;
  const tMin = hhmmToMinutes(slotHHMM);
  return daySlots.some((s) => {
    const sMin = hhmmToMinutes(s.time_start);
    const eMin = hhmmToMinutes(s.time_end);
    return tMin >= sMin && tMin < eMin;
  });
}

function getDropVisualState(status: 'checking' | 'ok' | 'conflict' | 'availability_override' | 'error' | null, isActiveDropSlot: boolean): {
  ringClass: string;
  overlayClass: string;
  label: string;
} {
  if (status === 'conflict') {
    return {
      ringClass: 'ring-2 ring-red-500/70 ring-inset',
      overlayClass: 'opacity-90 border-red-500/70 bg-red-50',
      label: 'Konflikt',
    };
  }

  if (status === 'availability_override') {
    return {
      ringClass: 'ring-2 ring-amber-500/70 ring-inset',
      overlayClass: 'opacity-90 border-amber-500/70 bg-amber-50',
      label: 'Freigabe-Override',
    };
  }

  if (status === 'ok') {
    return {
      ringClass: 'ring-2 ring-emerald-500/70 ring-inset',
      overlayClass: 'opacity-90 border-emerald-500/70 bg-emerald-50',
      label: 'Frei',
    };
  }

  if (status === 'checking') {
    return {
      ringClass: 'ring-2 ring-sky-500/60 ring-inset',
      overlayClass: 'opacity-85 border-sky-500/70 bg-sky-50',
      label: 'Prüfen...',
    };
  }

  if (isActiveDropSlot) {
    return {
      ringClass: 'ring-1 ring-primary/40 ring-inset',
      overlayClass: 'opacity-70 border-primary/60',
      label: 'Frei',
    };
  }

  return {
    ringClass: '',
    overlayClass: 'opacity-0 hover:opacity-20',
    label: 'Frei',
  };
}

const DayColumn: React.FC<DayColumnProps> = ({
  day,
  dayIndex,
  timeSlots,
  courses,
  calcTop,
  calcHeight,
  onContextMenuSlot,
  onContextMenuCourse,
  onClickCourse,
  onSlotClick,
  onDragOverSlot,
  onDropSlot,
  isDraggingCourse,
  activeDropTarget,
  activeDropConflictStatus,
  dayKey,
  draggable,
  onCourseDragStart,
  onCourseDragEnd,
  onTrialDragStart,
  onTrialDragEnd,
  getContingentInfo,
  getContingentColor,
  children,
  trials = [],
  onContextMenuTrial,
  rowHeight = 40,
  availabilitySlots,
  dayLayout,
}) => {
  const dayCourses = courses.filter((c) => c.dayOfWeek === dayIndex + 1);
  // Liegt eine gemeinsame Anordnung vor (Kurse und Probestunden zusammen), gilt sie —
  // sonst ordnet die Spalte ihre Kurse wie bisher unter sich.
  const laidOutCourses = dayLayout
    ? dayCourses.map((c) => ({
        ...c,
        ...(dayLayout.get(dayEntryKey.kurs(String(c.id))) ?? { column: 0, columns: 1 }),
      }))
    : layoutOverlappingEvents(dayCourses.map((c) => ({ ...c, id: String(c.id) })));
  const hasAvailabilityRules = !!availabilitySlots && availabilitySlots.length > 0;

  return (
    <div className="border-r last:border-r-0 relative" style={{ minHeight: `${timeSlots.length * rowHeight}px` }}>
      {timeSlots.map((slot) => {
        const isActiveDropSlot = !!isDraggingCourse
          && !!activeDropTarget
          && !!dayKey
          && activeDropTarget.dayKey === dayKey
          && activeDropTarget.hour === slot.hour
          && activeDropTarget.minute === slot.minute;
        const outsideAvailability = !isWithinAvailability(dayIndex + 1, slot.time, availabilitySlots);
        const showOutsideAvailability = !!availabilitySlots && availabilitySlots.length > 0 && outsideAvailability;
        let slotBgClass = '';
        if (hasAvailabilityRules) {
          slotBgClass = showOutsideAvailability ? 'bg-zinc-100/80' : 'bg-white';
        }
        const dropStatus = isActiveDropSlot ? (activeDropConflictStatus ?? null) : null;
        const dropVisual = getDropVisualState(dropStatus, isActiveDropSlot);

        return (
          <button
            type="button"
            key={slot.time}
            className={`block w-full appearance-none border-b p-1 text-left ${slotBgClass} ${dropVisual.ringClass}`}
            style={{ height: rowHeight }}
            onContextMenu={(e) => onContextMenuSlot(e, day, slot.hour, slot.minute)}
            onDragOver={(e) => onDragOverSlot?.(e, day, slot.hour, slot.minute)}
            onDrop={(e) => onDropSlot?.(e, day, slot.hour, slot.minute)}
            onClick={() => onSlotClick?.(day, slot.hour, slot.minute)}
          >
            <div className={`h-full w-full rounded-md border border-dashed flex items-center justify-center transition-opacity ${dropVisual.overlayClass}`}>
              <span className="text-xs text-muted-foreground">{dropVisual.label}</span>
            </div>
          </button>
        );
      })}

      {laidOutCourses.map((course) => {
        const top = calcTop(course.startTime);
        const height = calcHeight(course.startTime, course.endTime);
        const colStyle = overlapLayoutToColumnStyle(course);
        const info = getContingentInfo ? getContingentInfo(course.studentName) : null;
        const isExpired = false;
        const isNoUnitsLeft = info ? info.remainingUnits <= 0 : false;
        
        // Probestunden für diesen Kurs finden
        const courseTrials = trials.filter(trial => {
          if (!trial.courseId || trial.courseId !== course.courseId) return false;
          const trialStart = trial.startTime.slice(0, 5);
          const trialEnd = trial.endTime.slice(0, 5);
          return course.startTime.slice(0, 5) === trialStart && course.endTime.slice(0, 5) === trialEnd;
        });
        
        return (
          <CourseBlock
            key={course.id}
            course={course}
            top={top}
            height={height}
            transparent={isExpired || isNoUnitsLeft}
            left={colStyle.left}
            width={colStyle.width}
            onContextMenu={(e) => onContextMenuCourse(e, course)}
            onClick={() => onClickCourse?.(course)}
            draggable={draggable}
            onDragStart={onCourseDragStart}
            onDragEnd={onCourseDragEnd}
            onTrialDragStart={onTrialDragStart}
            onTrialDragEnd={onTrialDragEnd}
            contingentInfo={info || undefined}
            getContingentColor={getContingentColor}
            trials={courseTrials}
            onContextMenuTrial={onContextMenuTrial}
            calcTop={calcTop}
            calcHeight={calcHeight}
          />
        );
      })}
      {children}
    </div>
  );
};

export default DayColumn;


